import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import React, { useCallback, useState } from 'react';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { IFormData } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import EDDataGrid from '../../../../../../../BaseComponents/XGrid/EDDataGrid';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { update } from 'ramda';
import { ObjektVyKort } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { getCheckedValues } from './RepeteraAktivitetUtils';
import { RepeteraAktivitetPreviewQuery_rtn_BORepeteraAktivitetPreview_aktiviteter } from '../../Queries/_QueryTypes/RepeteraAktivitetPreviewQuery';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../../../../../Hooks/useFullscreenView';
import { capitalizeFirstLetter } from '../../../../../../../Utils/dateUtils';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid-pro';

export interface IRow
  extends RepeteraAktivitetPreviewQuery_rtn_BORepeteraAktivitetPreview_aktiviteter {
  Plats: string | null | undefined;
  Dag: string;
  Datum: string;
  id: string;
  [key: string]: unknown;
}
interface IProps {
  aktivitet: IFormData;
  values: IRepeteraAktivitetValues;
  setters: IRepeteraAktivitetSetters;
  rows: IRow[];
  setRows: (rows: IRow[]) => void;
}

export interface IRepeteraAktivitetValues {
  datumFr: string;
  datumTi: string;
  weekValue: {
    name: string;
    value: number;
  } | null;
  mondayChecked: boolean;
  tuesdayChecked: boolean;
  wednesdayChecked: boolean;
  thursdayChecked: boolean;
  fridayChecked: boolean;
  saturdayChecked: boolean;
  sundayChecked: boolean;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      selectedColor: {
        backgroundColor: theme.customProperties.errorComponent.accordionError,
      },
    } as const)
);

// Function to calculate column width
const calculateColumnWidth = (
  rows: IRow[],
  fieldName: string,
  maxWidth?: number
): number => {
  let maxFieldLength = 0;
  rows.forEach(row => {
    const fieldValue = row[fieldName] as string | undefined;
    const fieldLength = fieldValue ? fieldValue.length : 0;
    if (fieldLength > maxFieldLength) {
      maxFieldLength = fieldLength;
    }
  });
  const widthPerChar = maxFieldLength < 10 ? 11 : 9;
  let calculatedWidth = maxFieldLength * widthPerChar;

  if (maxWidth && calculatedWidth > maxWidth * widthPerChar) {
    return maxWidth * widthPerChar;
  }
  if (calculatedWidth < 60) {
    return 60;
  } else return calculatedWidth;
};

interface IRepeteraAktivitetSetters {
  setDatumFr: React.Dispatch<React.SetStateAction<string>>;
  setDatumTi: React.Dispatch<React.SetStateAction<string>>;
  setWeekValue: React.Dispatch<
    React.SetStateAction<{
      name: string;
      value: number;
    } | null>
  >;
  setMondayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setTuesdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setWednesdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setThursdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setFridayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setSaturdayChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setSundayChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

const RepeteraAktivitetFormStep2: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isSmallScreen = useIsSmallScreen();
  const isExtraSmallScreen = useIsExtraSmallScreen();
  const { classes } = useStyles();

  const [rubrikWidth, setRubrikWidth] = useState(
    calculateColumnWidth(props.rows, 'Rubrik', 15)
  );
  const [platsWidth, setPlatsWidth] = useState(
    rubrikWidth < 60 ? 768 - 433 : 373 - rubrikWidth
  );

  const handleColumnWidthChange: GridEventListener<'columnWidthChange'> =
    useCallback(params => {
      if (params.colDef.field === 'Rubrik') {
        setRubrikWidth(params.width);
      } else if (params.colDef.field === 'Plats') {
        setPlatsWidth(params.width);
      }
    }, []);

  const rowCardSmallScreen = props.rows.map((item, index) => {
    const selectedRow = item.Vald;
    const selectedRowColor = selectedRow ? classes.selectedColor : '';
    return (
      <EDBox key={item.Datum} sx={{ marginBottom: 0, maxWidth: '100%' }}>
        <ObjektVyKort key={item.Datum} noMargin noPadding>
          <EDBox
            className={selectedRowColor}
            sx={{
              display: 'flex',
              padding: '4px',
            }}
          >
            <EDBox sx={{ display: 'flex', width: '100%' }}>
              <EDBox
                sx={{ display: 'flex', alignItems: 'center', width: '10%' }}
              >
                <EDCheckbox
                  checked={Boolean(item.Vald)}
                  onCheck={checked => {
                    const updatedRows = props.rows.map((row, idx) => {
                      if (idx === index) {
                        return { ...row, Vald: checked };
                      }
                      return row;
                    });
                    props.setRows(updatedRows);
                  }}
                />
              </EDBox>
              <EDBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <EDBox sx={{ paddingTop: '4px', marginBottom: '5px' }}>
                  <EDTypography variant="caption" bold>
                    {item?.Rubrik}
                  </EDTypography>
                </EDBox>
                <EDBox width="100%" display="flex">
                  <EDBox
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '55%',
                    }}
                  >
                    <EDBox sx={{ display: 'flex' }}>
                      <EDTypography variant="caption" bold>
                        Datum:
                      </EDTypography>

                      <EDTypography variant="caption">
                        {item.Datum}
                      </EDTypography>
                    </EDBox>
                    <EDBox sx={{ display: 'flex' }}>
                      <EDTypography variant="caption" bold>
                        Tid:
                      </EDTypography>

                      <EDTypography variant="caption">
                        {props.aktivitet.FRANTID}-{props.aktivitet.TILLTID}
                      </EDTypography>
                    </EDBox>
                  </EDBox>

                  <EDBox
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '40%',
                    }}
                  >
                    <EDBox sx={{ display: 'flex' }}>
                      <EDTypography variant="caption" bold>
                        Dag:
                      </EDTypography>

                      <EDTypography variant="caption">{item.Dag}</EDTypography>
                    </EDBox>
                    <EDBox sx={{ display: 'flex' }}>
                      <EDTypography variant="caption" bold>
                        Plats:
                      </EDTypography>

                      <EDTypography variant="caption">
                        {item.Plats}
                      </EDTypography>
                    </EDBox>
                  </EDBox>
                </EDBox>
              </EDBox>
            </EDBox>
          </EDBox>
        </ObjektVyKort>
      </EDBox>
    );
  });

  const rowGridBigScreen = (
    <EDBox>
      <ObjektVyKort>
        <EDDataGrid
          onColumnWidthChange={handleColumnWidthChange}
          columns={[
            {
              disableColumnMenu: true,
              align: undefined,
              field: 'Vald',
              width: 70,
              minWidth: 70,
              maxWidth: 70,
              renderCell: params => {
                return (
                  <EDCheckbox
                    checked={params.row.Vald}
                    onCheck={checked => {
                      const rowIndex = props.rows?.findIndex(
                        row => row.id === params.id
                      );
                      const updatedRows = update(
                        rowIndex,
                        {
                          ...params.row,
                          Vald: checked,
                        },
                        props.rows
                      );
                      props.setRows(updatedRows);
                    }}
                  />
                );
              },
            },
            {
              align: undefined,
              headerName: 'Titel',
              field: 'Rubrik',
              width: rubrikWidth,
              disableColumnMenu: true,
              sortable: true,
            },
            {
              align: undefined,
              field: 'Datum',
              width: 115,
              disableColumnMenu: true,
              sortable: true,
            },
            {
              align: undefined,
              field: 'Dag',
              width: 100,
              disableColumnMenu: true,
              sortable: true,
            },
            {
              align: undefined,
              field: 'Tid',
              width: 110,
              disableColumnMenu: true,
              sortable: true,
            },
            {
              align: undefined,
              field: 'Plats',
              width: platsWidth,
              minWidth: 100,
              disableColumnMenu: true,
              sortable: true,
            },
          ]}
          rows={props.rows ?? []}
          getSelectedRowClassName={(params: GridRowParams) =>
            params.row.Vald ? classes.selectedColor : ''
          }
          autoHeight
          getRowClassName={params => {
            return params.row.Vald === true
              ? '.MuiDataGrid-row Mui-selected'
              : '';
          }}
        ></EDDataGrid>
      </ObjektVyKort>
    </EDBox>
  );

  const infoLargeScreen = (
    <EDBox
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: '100%',
      }}
    >
      <EDBox paddingLeft={2}>
        <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
          <EDTypography sx={{ paddingRight: '2px' }} variant="body2" bold>
            Mellan:
          </EDTypography>

          <EDTypography paddingLeft={'1px'} variant="body2" color="black">
            {props.values.datumFr} till {props.values.datumTi}
          </EDTypography>
        </EDBox>
        <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
          <EDTypography sx={{ paddingRight: '4px' }} variant="body2" bold>
            Dag:
          </EDTypography>
          {getCheckedValues(props.values).map(item => {
            if (item.value) {
              return (
                <EDTypography
                  sx={{ paddingRight: '5px' }}
                  key={item.day}
                  variant="body2"
                >
                  {capitalizeFirstLetter(item.day)}
                </EDTypography>
              );
            } else return null;
          })}
        </EDBox>
      </EDBox>
      <EDBox paddingRight="20%">
        <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
          <EDTypography sx={{ paddingRight: '2px' }} variant="body2" bold>
            Veckor:
          </EDTypography>

          <EDTypography paddingLeft={'2px'} variant="body2" color="black">
            {props.values.weekValue?.name}
          </EDTypography>
        </EDBox>

        <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
          <EDTypography sx={{ paddingRight: '2px' }} variant="body2" bold>
            Tid:
          </EDTypography>
          <EDTypography variant="body2">
            {props.aktivitet.FRANTID}-{props.aktivitet.TILLTID}
          </EDTypography>
        </EDBox>
      </EDBox>
    </EDBox>
  );

  const infoSmallScreen = (
    <EDBox sx={{ marginbottom: 2 }}>
      <EDBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flexFlow: 'inherit',
        }}
      >
        <EDBox
          sx={{
            display: 'initial',
            justifyContent: 'space-between',
            marginBottom: '6px',
            width: '60%',
          }}
        >
          <EDBox
            sx={{
              display: 'flex',
            }}
          >
            <EDBox
              sx={{
                display: 'flex',
              }}
            >
              <EDTypography bold marginRight={'2px'} variant="caption">
                Mellan:
              </EDTypography>

              <EDTypography variant="caption">
                {props.values.datumFr} till {props.values.datumTi}
              </EDTypography>
            </EDBox>
          </EDBox>
          <EDBox
            sx={{
              width: '100%',
            }}
          >
            <EDBox
              sx={{
                display: 'flex',
              }}
            >
              <EDTypography marginRight={'2px'} bold variant="caption">
                Dag:
              </EDTypography>
              <EDBox
                sx={{
                  display: 'flex',
                  flexFlow: 'wrap',
                  width: '100%',
                  variant: 'caption',
                }}
              >
                {getCheckedValues(props.values).map(item => {
                  if (item.value) {
                    return (
                      <EDTypography
                        sx={{ paddingRight: '5px' }}
                        key={item.day}
                        variant="caption"
                      >
                        {capitalizeFirstLetter(item.day)}
                      </EDTypography>
                    );
                  } else return null;
                })}
              </EDBox>
            </EDBox>
          </EDBox>
        </EDBox>
        <EDBox
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '40%',
          }}
        >
          <EDBox>
            {' '}
            <EDBox
              sx={{
                display: 'flex',
                alignItems: isExtraSmallScreen ? 'flex-start' : 'center',
                flexDirection: isExtraSmallScreen ? 'column' : '',
              }}
            >
              <EDBox
                sx={{
                  display: isSmallScreen ? 'flex' : '',
                }}
              >
                <EDTypography bold marginRight={'2px'} variant="caption">
                  Veckor:
                </EDTypography>
                <EDTypography variant="caption">
                  {props.values.weekValue?.name}
                </EDTypography>
              </EDBox>
            </EDBox>
            <EDBox
              sx={{
                display: isSmallScreen ? 'flex' : '',
              }}
            >
              <EDTypography variant="caption" bold>
                Tid:
              </EDTypography>
              <EDTypography color="black" variant="caption">
                {props.aktivitet.FRANTID}-{props.aktivitet.TILLTID}
              </EDTypography>
            </EDBox>
          </EDBox>
        </EDBox>
      </EDBox>
    </EDBox>
  );

  return (
    <EDBox
      sx={{
        display: isSmallScreen ? 'flex' : '',
        flexDirection: isSmallScreen ? 'column-reverse' : '',
        margin: isSmallScreen ? '-6%' : '',
      }}
    >
      {!isSmallScreen && !isExtraSmallScreen ? (
        <EDBox sx={{ paddingBottom: isSmallScreen ? '' : '2%' }}>
          {rowGridBigScreen}
        </EDBox>
      ) : (
        <EDBox>{rowCardSmallScreen}</EDBox>
      )}
      <EDBox
        sx={{
          marginBottom: 1,
          position: isSmallScreen ? 'sticky' : '',
          top: isSmallScreen ? '0px' : '',
        }}
      >
        <ObjektVyKort
          noMargin={!isSmallScreen && !isExtraSmallScreen ? false : true}
          caption="ÖNSKAD REPETITION"
        >
          {!isSmallScreen && !isExtraSmallScreen
            ? infoLargeScreen
            : infoSmallScreen}
        </ObjektVyKort>
      </EDBox>
      {/* Hardcoded width to fit all computer screen sizes */}
      <EDBox sx={{ width: isSmallScreen ? '' : '800px' }}></EDBox>
    </EDBox>
  );
};

export default RepeteraAktivitetFormStep2;
